import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AddStockComponent } from '../add-stock/add-stock.component';
import { NotificationService } from '../notification/notification.service';

interface GameChip {
  id: number | null;
  label: string;
  color: string | null;
}

interface ReallocOrderRow {
  orderId: number;
  displayId: string;
  customer: string;
  country: string;
  placedLabel: string;
  /** napok a feladási határidőig; null = nincs határidő */
  days: number | null;
  need: number;
  alloc: number;
  /** a rendszer időrend szerint ehhez foglalta a darabo(ka)t */
  auto: boolean;
}

interface ReallocState {
  product: any;
  stock: number;
  orders: ReallocOrderRow[];
}

const HU_MONTHS_SHORT = ['jan.', 'febr.', 'márc.', 'ápr.', 'máj.', 'jún.', 'júl.', 'aug.', 'szept.', 'okt.', 'nov.', 'dec.'];

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrl: './inventory.component.scss'
})
export class InventoryComponent implements OnInit, OnDestroy {

  fetching = false;
  pageSize = 20;
  pageIndex = 0;
  totalProducts = 0;
  readonly pageSizeOptions: number[] = [20, 50, 100];

  searchControl = new FormControl('');
  limitBelowOnly = false;
  selectedGameId: number | null = null;
  gameChips: GameChip[] = [{ id: null, label: 'Mind', color: null }];

  dataSource = signal<any[]>([]);
  openBom: Record<number, boolean> = {};

  // Készlet átruházás modál
  realloc: ReallocState | null = null;
  reallocDraft: ReallocOrderRow[] | null = null;
  reallocLoading = false;

  private searchSub: Subscription | null = null;
  /** A legutóbb indított product-stock lekérés sorszáma (elavult válaszok kiszűrése). */
  private fetchToken = 0;
  private readonly gameBadgePalette = ['#9A6E9E', '#5E7FA8', '#8FB859', '#C9A45C', '#C97C7C', '#7A8DA0'];

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    localStorage.removeItem('inventoryFilter:game');
    this.searchSub = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.onFiltersChanged());
    this.fetchGames();
    this.fetchProducts();
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  // ---------------------------------------------------------------------
  // Adatbetöltés + szűrés
  // ---------------------------------------------------------------------

  fetchGames() {
    this.http.get<any>('games').subscribe({
      next: (response) => {
        const games: any[] = Array.isArray(response) ? response : (response?.items || []);
        this.gameChips = [
          { id: null, label: 'Mind', color: null },
          // Csak a készletkövetett játékok (Jump and Hide); a BGFox a saját oldalán van.
          ...games.filter((game) => `${game.name || ''}`.trim() === 'Jump and Hide').map((game) => ({
            id: game.id,
            label: game.name,
            color: this.gameColor(game.name),
          })),
        ];
      },
      error: () => {
        this.gameChips = [{ id: null, label: 'Mind', color: null }];
      },
    });
  }

  fetchProducts() {
    // A kérések nem törlődnek automatikusan: ha a kezdeti (szűretlen) lekérés
    // lassabban ér vissza, mint a keresésre indított újabb, felülírná a szűrt
    // találatokat. Sorszámmal dobjuk el az elavult válaszokat.
    const token = ++this.fetchToken;
    this.fetching = true;
    this.http.get<{ items: any[]; total: number; page: number; pageSize: number }>(`components/product-stock?${this.getCurrentQueryFilter()}`).subscribe({
      next: (response) => {
        if (token !== this.fetchToken) {
          return;
        }
        this.totalProducts = response?.total || 0;
        this.dataSource.set(response?.items || []);
        if (!this.dataSource().length && this.totalProducts > 0 && this.pageIndex > 0) {
          this.pageIndex = Math.max(Math.ceil(this.totalProducts / this.pageSize) - 1, 0);
          this.fetchProducts();
          return;
        }
        this.fetching = false;
      },
      error: () => {
        if (token !== this.fetchToken) {
          return;
        }
        this.fetching = false;
      },
    });
  }

  getCurrentQueryFilter(): string {
    const params = new URLSearchParams();
    const name = this.searchControl.value?.trim();
    if (this.limitBelowOnly) {
      params.set('limit', '1');
    }
    if (name) {
      params.set('name', name);
    }
    if (this.selectedGameId !== null) {
      params.set('game', `${this.selectedGameId}`);
    }
    params.set('page', `${this.pageIndex}`);
    params.set('page_size', `${this.pageSize}`);
    return params.toString();
  }

  pickGame(chip: GameChip) {
    if (this.selectedGameId === chip.id) {
      return;
    }
    this.selectedGameId = chip.id;
    this.onFiltersChanged();
  }

  toggleLimitBelow() {
    this.limitBelowOnly = !this.limitBelowOnly;
    this.onFiltersChanged();
  }

  onFiltersChanged(resetPage: boolean = true) {
    if (resetPage) {
      this.pageIndex = 0;
    }
    this.fetchProducts();
  }

  gameColor(gameName: string): string {
    let hash = 0;
    const value = gameName || '';
    for (let i = 0; i < value.length; i++) {
      hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
    }
    return this.gameBadgePalette[hash % this.gameBadgePalette.length];
  }

  // ---------------------------------------------------------------------
  // Lapozás
  // ---------------------------------------------------------------------

  get visibleProductCount(): number {
    return this.dataSource().reduce((total, row) => total + (row?.products?.length || 0), 0);
  }

  get pageRangeLabel(): string {
    if (!this.totalProducts) {
      return '0 / 0';
    }
    const from = this.pageIndex * this.pageSize + 1;
    const to = Math.min((this.pageIndex + 1) * this.pageSize, this.totalProducts);
    return `${from}–${to} / ${this.totalProducts}`;
  }

  get canPrevPage(): boolean {
    return this.pageIndex > 0;
  }

  get canNextPage(): boolean {
    return (this.pageIndex + 1) * this.pageSize < this.totalProducts;
  }

  prevPage() {
    if (!this.canPrevPage) {
      return;
    }
    this.pageIndex--;
    this.fetchProducts();
  }

  nextPage() {
    if (!this.canNextPage) {
      return;
    }
    this.pageIndex++;
    this.fetchProducts();
  }

  onPageSizeChange(event: Event) {
    const value = parseInt((event.target as HTMLSelectElement).value, 10);
    if (!isNaN(value) && value > 0) {
      this.pageSize = value;
      this.pageIndex = 0;
      this.fetchProducts();
    }
  }

  // ---------------------------------------------------------------------
  // Sor-műveletek
  // ---------------------------------------------------------------------

  openProductEditor() {
    this.router.navigate(['/inventory', '0']);
  }

  toggleBom(product: any) {
    this.openBom[product.id] = !this.openBom[product.id];
  }

  costTitle(product: any): string {
    if (product?.cost_missing) {
      return 'Valamelyik összetevőnek nincs ára (beszerzési ár vagy súly/nyomtatási idő) — a szám alsó becslés';
    }
    if (product?.cost_estimated) {
      return 'Nyomtatott elem anyagár nélkül: 3200 Ft/kg alapárral számolva';
    }
    return 'Nyomtatás (anyag + gépidő) + vásárolt tételek (mágnes, üveg, stb.) a recept szerint';
  }

  isBomOpen(product: any): boolean {
    return !!this.openBom[product.id];
  }

  bomPerPackage(child: any): number {
    const qty = child?.quantity || 1;
    return Math.floor((child?.stock || 0) / Math.max(1, qty));
  }

  /**
   * A recept-sor `stock` mezője a SZABAD készlet (a tárolt eljárás már levonta a
   * rendelések foglalásait), a Komponensek képernyő viszont a FIZIKAI készletet
   * mutatja. Ugyanaz a komponens tehát két képernyőn két számmal jelenik meg —
   * a tooltip levezeti, hogyan jön ki a kettő egymásból.
   */
  bomChildStockTitle(child: any): string {
    const free = child?.stock || 0;
    const reserved = child?.reserved || 0;
    if (reserved > 0) {
      return `${free} db szabad készlet · ${reserved} db rendelésekhez foglalva · fizikailag ${free + reserved} db van a raktárban`;
    }
    return `${free} db szabad készlet — nincs rá aktív foglalás`;
  }

  canManageStock(item: any, isChild: boolean = false): boolean {
    const target = isChild ? item?.component_child : item;
    if (!target) {
      return false;
    }

    return target.is_package_unit === 1 || target.has_bom === 0;
  }

  openAddStockDialog(item: any, isPositive: boolean, isChild: boolean = false) {
    const target = isChild ? item?.component_child : item;
    if (!target?.id) {
      return;
    }

    if (!this.canManageStock(item, isChild)) {
      this.notificationService.show("Csak nyers komponenshez vagy csomagolási egységhez lehet készletet felvinni!", "warning");
      return;
    }

    const dialogRef = this.dialog.open(AddStockComponent, {
      data: { positive: isPositive, componentId: target.id },
      panelClass: 'stk-add-stock-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.fetchProducts();
      }
    });
  }

  // ---------------------------------------------------------------------
  // Készlet átruházás modál (22-keszlet-atruhazas-modal + reallocVals 1:1)
  // ---------------------------------------------------------------------

  /**
   * A szétosztható készlet a FIZIKAI készlet: a `stock` mezőből a tárolt eljárás
   * már levonta az aktív rendelések foglalásait (`reserved`), így a raktárban
   * ténylegesen ott lévő darabszám = stock + reserved. A modál ezt a poolt
   * játssza vissza időrendben a rendelésekre — ha csak a `stock`-kal
   * számolnánk, a már lefoglalt darabok eltűnnének, és minden rendelés
   * „Hiány” lenne.
   */
  private physicalStockOf(product: any): number {
    return Math.max(0, (product?.stock || 0) + (product?.reserved || 0));
  }

  openRealloc(product: any) {
    const physicalStock = this.physicalStockOf(product);
    this.realloc = { product, stock: physicalStock, orders: [] };
    this.reallocDraft = null;
    this.reallocLoading = true;

    const params = new URLSearchParams();
    params.set('search', product.sku || product.name || '');
    // A backend csak az '1' értéket veszi szűrésnek (only_not_packaged === '1').
    params.set('only_not_packaged', '1');
    params.set('archived', '0');
    params.set('sort_by', 'created_at');
    params.set('page', '0');
    params.set('page_size', '100');

    this.http.get<{ items: any[] }>(`orders?${params.toString()}`).subscribe({
      next: (response) => {
        const orders = (response?.items || [])
          .slice()
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

        const rows: ReallocOrderRow[] = [];
        // A rendszer időrendben foglalta le a készletet (FIFO) — ugyanazt játsszuk vissza.
        let remaining = physicalStock;
        for (const order of orders) {
          const need = (order.orderProducts || [])
            .filter((op: any) => op.component_id === product.id || op.sku === product.sku)
            .reduce((sum: number, op: any) => sum + (op.quantity || 0), 0);
          if (need <= 0) {
            continue;
          }
          const alloc = Math.max(0, Math.min(need, remaining));
          remaining -= alloc;
          rows.push({
            orderId: order.id,
            displayId: '#' + (order.receipt_id || order.order_number || order.id),
            customer: order.customer,
            country: order.shipping_country || order.country_iso || '—',
            placedLabel: this.formatPlaced(order.created_at),
            days: this.daysUntil(order.shipping_expected_at),
            need,
            alloc,
            auto: alloc > 0,
          });
        }

        if (this.realloc) {
          this.realloc = { ...this.realloc, orders: rows };
          this.reallocDraft = rows.map((row) => ({ ...row }));
        }
        this.reallocLoading = false;
      },
      error: () => {
        this.reallocLoading = false;
        if (this.realloc) {
          this.realloc = { ...this.realloc, orders: [] };
          this.reallocDraft = [];
        }
      },
    });
  }

  closeRealloc() {
    this.realloc = null;
    this.reallocDraft = null;
    this.reallocLoading = false;
  }

  get reallocUsed(): number {
    return (this.reallocDraft || []).reduce((sum, row) => sum + row.alloc, 0);
  }

  get reallocFree(): number {
    return (this.realloc?.stock || 0) - this.reallocUsed;
  }

  get reallocTotalNeed(): number {
    return (this.realloc?.orders || []).reduce((sum, row) => sum + row.need, 0);
  }

  reallocCanMinus(row: ReallocOrderRow): boolean {
    return row.alloc > 0;
  }

  reallocCanPlus(row: ReallocOrderRow): boolean {
    return this.reallocFree > 0 && row.alloc < row.need;
  }

  reallocMinus(index: number) {
    const row = this.reallocDraft?.[index];
    if (!row || !this.reallocCanMinus(row)) {
      return;
    }
    row.alloc--;
  }

  reallocPlus(index: number) {
    const row = this.reallocDraft?.[index];
    if (!row || !this.reallocCanPlus(row)) {
      return;
    }
    row.alloc++;
  }

  reallocChanged(index: number): boolean {
    const draft = this.reallocDraft?.[index];
    const orig = this.realloc?.orders?.[index];
    return !!draft && !!orig && draft.alloc !== orig.alloc;
  }

  get reallocHasChange(): boolean {
    return (this.reallocDraft || []).some((_, index) => this.reallocChanged(index));
  }

  get reallocSummary(): string {
    const draft = this.reallocDraft || [];
    const orders = this.realloc?.orders || [];
    let taken = 0;
    let given = 0;
    const donorIds: string[] = [];
    const recipientIds: string[] = [];
    draft.forEach((row, index) => {
      const delta = row.alloc - (orders[index]?.alloc ?? row.alloc);
      if (delta < 0) {
        taken += -delta;
        donorIds.push(row.displayId);
      }
      if (delta > 0) {
        given += delta;
        recipientIds.push(row.displayId);
      }
    });

    if (taken === 0 && given === 0) {
      return 'Még nincs módosítás — vedd el a darabot a − gombbal, és add át a + gombbal.';
    }

    const parts: string[] = [];
    if (given > 0) {
      parts.push(given + ' db átruházva: ' + donorIds.join(', ') + ' → ' + recipientIds.join(', '));
    } else {
      parts.push(taken + ' db visszakerül a szabad készletbe: ' + donorIds.join(', '));
    }
    if (this.reallocFree > 0) {
      parts.push(this.reallocFree + ' db még kiosztható');
    }
    return parts.join(' · ');
  }

  deadlineLabel(days: number | null): string {
    if (days === null) {
      return 'nincs feladási határidő';
    }
    if (days < 0) {
      return Math.abs(days) + ' napja késik a feladás';
    }
    if (days === 0) {
      return 'ma kell feladni';
    }
    return days + ' nap van a feladásig';
  }

  deadlineTone(days: number | null): string {
    if (days === null) {
      return 'grey';
    }
    if (days < 0) {
      return 'red';
    }
    return days === 0 ? 'yellow' : 'green';
  }

  saveRealloc() {
    if (!this.reallocHasChange || !this.realloc || !this.reallocDraft) {
      return;
    }
    const product = this.realloc.product;
    const payload = {
      component_id: product.id,
      sku: product.sku,
      allocations: this.reallocDraft.map((row) => ({ order_id: row.orderId, quantity: row.alloc })),
    };
    this.http.post('orders/stock-reallocation', payload).subscribe({
      next: () => {
        this.notificationService.show('Készlet átruházás elmentve!', 'success');
        this.closeRealloc();
        this.fetchProducts();
      },
      error: () => {
        this.notificationService.show('Az átruházás mentése sikertelen!', 'danger');
      },
    });
  }

  private formatPlaced(value: string | Date): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${HU_MONTHS_SHORT[date.getMonth()]} ${day}, ${hours}:${minutes}`;
  }

  private daysUntil(value: string | Date | null): number | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    const startOfDay = (input: Date) => new Date(input.getFullYear(), input.getMonth(), input.getDate()).getTime();
    return Math.round((startOfDay(date) - startOfDay(new Date())) / 86400000);
  }
}
